import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Image as ImageIcon, Key, MapPin, Copy, Trash2, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function ProToolsPanel() {
  const [status, setStatus] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [locInput, setLocInput] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/api/pro/status`, auth()).then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!status) return null;

  const gate = (fn) => status.is_pro ? fn() : toast.error('Upgrade to Contractor Pro to unlock this feature');

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/api/pro/branding/logo`, fd, auth());
      toast.success('Company logo saved — it now appears on your invoices and profile');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const genKey = async () => {
    try {
      const r = await axios.post(`${API}/api/pro/api-key`, null, auth());
      setNewKey(r.data.api_key);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveLocations = async (locations) => {
    try {
      await axios.put(`${API}/api/pro/service-locations`, { locations }, auth());
      toast.success('Service areas updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6 space-y-6" data-testid="pro-tools-panel">
      <div className="flex items-center gap-2">
        <Crown className="h-5 w-5 text-amber-500" />
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Pro Tools</h3>
        {!status.is_pro && <span className="text-[10px] font-black uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Pro plan required</span>}
      </div>

      {/* Custom Branding */}
      <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4" data-testid="pro-branding-section">
        <p className="font-semibold text-sm text-neutral-900 dark:text-white flex items-center gap-1.5 mb-1"><ImageIcon className="h-4 w-4 text-blue-600" /> Custom Branding</p>
        <p className="text-xs text-neutral-500 mb-3">Your company logo appears on invoices, estimates, and your public profile.</p>
        <div className="flex items-center gap-3">
          {status.company_logo && <img src={`${API}${status.company_logo}`} alt="Company logo" className="h-12 w-12 object-contain border rounded-lg bg-white" data-testid="pro-logo-preview" />}
          <label className={`text-xs font-bold px-3 py-2 border rounded-lg cursor-pointer ${status.is_pro ? 'border-blue-600 text-blue-600 hover:bg-blue-50' : 'border-neutral-300 text-neutral-400'}`}>
            {busy ? 'Uploading...' : status.company_logo ? 'Replace Logo' : 'Upload Logo'}
            <input type="file" accept="image/*" className="hidden" disabled={!status.is_pro || busy}
              onChange={(e) => gate(() => uploadLogo(e.target.files[0]))} data-testid="pro-logo-input" />
          </label>
          {status.company_logo && (
            <button onClick={() => axios.delete(`${API}/api/pro/branding/logo`, auth()).then(load)} className="text-xs text-red-500 font-bold" data-testid="pro-logo-remove">Remove</button>
          )}
        </div>
      </div>

      {/* Multi-location */}
      <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4" data-testid="pro-locations-section">
        <p className="font-semibold text-sm text-neutral-900 dark:text-white flex items-center gap-1.5 mb-1"><MapPin className="h-4 w-4 text-emerald-600" /> Multi-Location Service Areas</p>
        <p className="text-xs text-neutral-500 mb-3">Add up to 10 cities you serve — shown to homeowners and used for job matching.</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {(status.service_locations || []).map((l) => (
            <span key={l} className="inline-flex items-center gap-1 text-xs font-bold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-300 rounded-full px-2.5 py-1" data-testid={`pro-location-${l.replace(/[^a-z0-9]/gi, '-')}`}>
              {l}
              <button onClick={() => saveLocations(status.service_locations.filter(x => x !== l))} className="text-emerald-500 hover:text-red-500">×</button>
            </span>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); gate(() => { if (locInput.trim()) { saveLocations([...(status.service_locations || []), locInput.trim()]); setLocInput(''); } }); }} className="flex gap-2">
          <input value={locInput} onChange={(e) => setLocInput(e.target.value)} placeholder="City, ST"
            className="flex-1 text-sm px-3 py-2 border rounded-lg bg-white dark:bg-neutral-900 dark:border-neutral-600 text-neutral-900 dark:text-white" data-testid="pro-location-input" />
          <button type="submit" className="text-xs font-bold px-3 border rounded-lg border-emerald-600 text-emerald-600" data-testid="pro-location-add"><Plus className="h-4 w-4" /></button>
        </form>
      </div>

      {/* API Access */}
      <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4" data-testid="pro-api-section">
        <p className="font-semibold text-sm text-neutral-900 dark:text-white flex items-center gap-1.5 mb-1"><Key className="h-4 w-4 text-purple-600" /> API Access</p>
        <p className="text-xs text-neutral-500 mb-3">Pull your jobs and applications into your own tools. Endpoints: <code className="bg-neutral-100 dark:bg-neutral-900 px-1 rounded">GET /api/v1/jobs</code> and <code className="bg-neutral-100 dark:bg-neutral-900 px-1 rounded">GET /api/v1/applications</code> with header <code className="bg-neutral-100 dark:bg-neutral-900 px-1 rounded">X-API-Key</code>.</p>
        {newKey && (
          <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-300 rounded-lg p-2 mb-2" data-testid="pro-api-key-reveal">
            <code className="text-xs flex-1 break-all text-purple-800 dark:text-purple-300">{newKey}</code>
            <button onClick={() => { navigator.clipboard.writeText(newKey); toast.success('Copied'); }} className="text-purple-600"><Copy className="h-4 w-4" /></button>
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={() => gate(genKey)} className={`text-xs font-bold px-3 py-2 border rounded-lg ${status.is_pro ? 'border-purple-600 text-purple-600 hover:bg-purple-50' : 'border-neutral-300 text-neutral-400'}`} data-testid="pro-api-generate">
            {status.has_api_key ? 'Regenerate Key' : 'Generate API Key'}
          </button>
          {status.has_api_key && (
            <button onClick={() => axios.delete(`${API}/api/pro/api-key`, auth()).then(() => { setNewKey(null); load(); })} className="text-xs font-bold px-3 py-2 border border-red-300 text-red-500 rounded-lg inline-flex items-center gap-1" data-testid="pro-api-revoke">
              <Trash2 className="h-3.5 w-3.5" /> Revoke
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
